import { useMemo, useLayoutEffect, useRef, useState } from 'react';
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Check, Plus, Users, Zap } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import type { PricingSaasPlan, PricingSaasCharge, PricingSaasDiscount, PricingSaasMetric, PricingSaasThreshold } from '../types';

interface PricingCardsDisplayProps {
  plans: PricingSaasPlan[];
  pageMetrics?: PricingSaasMetric | PricingSaasMetric[];
}

type BillingPeriod = 'monthly' | 'annually';

// Helper to determine billing period from charge
const getChargeBillingType = (billing: string): BillingPeriod => {
  const lower = billing.toLowerCase();
  if (lower.includes('yearly') || lower.includes('annual') || lower.includes('billed_yearly')) {
    return 'annually';
  }
  return 'monthly';
};

// Helper to check if charge is billed annually (vs monthly)
const isBilledAnnually = (billing: string): boolean => {
  const lower = billing.toLowerCase();
  return lower.includes('billed_yearly') || lower.includes('billed_annual') ||
         (lower.includes('yearly') && !lower.includes('monthly'));
};

// Helper to format price for display
const formatPriceValue = (charge: PricingSaasCharge): {
  amount: string;
  perMonth: boolean;
  isContactSales: boolean;
  isFree: boolean;
  billedAnnually: boolean;
} => {
  if (charge.price === null || charge.price === undefined) {
    const isFree = charge.billing?.toLowerCase().includes('freemium') ||
                   charge.billing?.toLowerCase().includes('free');
    return {
      amount: isFree ? 'Free' : 'Contact Sales',
      perMonth: false,
      isContactSales: !isFree,
      isFree,
      billedAnnually: false,
    };
  }

  const priceAmount = charge.price_display || `$${charge.price.toLocaleString()}`;
  const billing = charge.billing?.toLowerCase() || '';
  const billedAnnually = isBilledAnnually(billing);

  // Check if this is a per-month price or per-year price
  const isYearlyAmount = billing.includes('yearly') && !billing.includes('monthly');

  return {
    amount: priceAmount,
    perMonth: !isYearlyAmount,
    isContactSales: false,
    isFree: false,
    billedAnnually,
  };
};

// Get charges separated by billing type
const getChargesByBillingType = (charges: PricingSaasCharge[]): {
  monthlyCharge: PricingSaasCharge | null;
  annualCharge: PricingSaasCharge | null;
} => {
  if (!charges || charges.length === 0) {
    return { monthlyCharge: null, annualCharge: null };
  }

  let monthlyCharge: PricingSaasCharge | null = null;
  let annualCharge: PricingSaasCharge | null = null;

  charges.forEach(charge => {
    const billingType = getChargeBillingType(charge.billing || '');
    if (billingType === 'monthly' && !monthlyCharge) {
      monthlyCharge = charge;
    } else if (billingType === 'annually' && !annualCharge) {
      annualCharge = charge;
    }
  });

  // If only one charge exists, use it as primary
  if (!monthlyCharge && !annualCharge && charges.length > 0) {
    monthlyCharge = charges[0];
  }

  return { monthlyCharge, annualCharge };
};

// Tier card component
const PricingTierCard = ({
  plan,
  discounts,
  topRef,
  topMinHeight,
  alignedThresholds,
}: {
  plan: PricingSaasPlan;
  discounts?: PricingSaasDiscount[];
  topRef?: (el: HTMLDivElement | null) => void;
  topMinHeight?: number;
  alignedThresholds?: (PricingSaasThreshold | null)[];
}) => {
  const planName = plan.display_name || plan.plan_name || plan.name;
  const { monthlyCharge, annualCharge } = getChargesByBillingType(plan.charges || []);

  // Use annual as primary when available, fall back to monthly
  const primaryCharge = annualCharge || monthlyCharge;
  const primaryPrice = primaryCharge ? formatPriceValue(primaryCharge) : null;
  const hasAnnualPrimary = !!annualCharge;

  // Calculate monthly alternative display (when annual is primary)
  const monthlyAltDisplay = useMemo(() => {
    if (!monthlyCharge || !hasAnnualPrimary) return null;

    const monthlyPrice = formatPriceValue(monthlyCharge);
    if (monthlyPrice.isContactSales || monthlyPrice.isFree) return null;

    return {
      amount: monthlyPrice.amount,
      perMonth: monthlyPrice.perMonth,
    };
  }, [monthlyCharge, hasAnnualPrimary]);

  // Get relevant discount for this plan (only show non-annual discounts, simplified)
  const relevantDiscount = useMemo(() => {
    const discount = discounts?.find(d => {
      if (!d.amount) return false;
      const category = d.discount_category?.toLowerCase() || '';
      const description = (d.description || '').toLowerCase();
      const name = (d.name || '').toLowerCase();
      // Skip annual discounts - they're implied by showing annual price
      const isAnnualDiscount = category.includes('annual') ||
                                description.includes('annual') ||
                                name.includes('annual');
      return !isAnnualDiscount && (d.description || d.name);
    });
    return discount;
  }, [discounts]);

  // Build features list from charge details (unit/metric now shown in price section)
  const features: string[] = [];
  if (primaryCharge?.included_quantity) {
    features.push(`${primaryCharge.included_quantity} included`);
  }

  const isContactSales = !primaryPrice || primaryPrice.isContactSales;
  const isFree = primaryPrice?.isFree || false;

  // Use aligned thresholds if provided, otherwise fall back to plan thresholds
  const thresholdsToRender = alignedThresholds || plan.thresholds || [];
  const hasAnyThresholds = thresholdsToRender.some(t => t !== null);

  return (
    <Card
      className="grid transition-all hover:shadow-lg border border-gray-200"
      style={{ gridTemplateRows: topMinHeight ? `${topMinHeight}px auto` : 'auto auto' }}
    >
      {/* Top section - synced height across cards */}
      <div ref={topRef}>
        <CardHeader className="pb-2 pt-4">
          <div className="text-center">
            <h3 className="text-lg font-semibold text-gray-900">{planName}</h3>
            {/* Only show charge type badge if there's an actual price (not contact sales) */}
            {primaryCharge?.charge_type && !isContactSales && !isFree && (
              <Badge variant="outline" className="mt-1 text-xs">
                {primaryCharge.charge_type}
              </Badge>
            )}
          </div>
        </CardHeader>

        <CardContent className="pb-4 pt-2">
          {/* Price Section */}
          <div className="text-center mb-4">
            {isFree ? (
              <div className="text-4xl font-bold text-gray-900">Free</div>
            ) : isContactSales ? (
              <div className="text-2xl font-semibold text-gray-700">Contact Sales</div>
            ) : (
              <>
                {/* Primary price */}
                {(() => {
                  const isPayAsYouGo = primaryCharge?.usage_type === 'pay_as_you_go';
                  const unitText = primaryCharge?.unit || primaryCharge?.pricing_metric?.display_name;

                  if (isPayAsYouGo) {
                    // Pay as you go display - no /mo, show pill
                    return (
                      <>
                        <div className="flex items-baseline justify-center gap-1">
                          <span className="text-4xl font-bold text-gray-900">{primaryPrice?.amount}</span>
                        </div>
                        {unitText && (
                          <div className="text-xs text-gray-500 mt-1">
                            per <span className="font-bold text-purple-600">{unitText}</span>
                          </div>
                        )}
                        <Badge className="mt-2 bg-amber-100 text-amber-700 border-amber-200">
                          Pay as you go
                        </Badge>
                      </>
                    );
                  }

                  // Standard subscription display
                  return (
                    <>
                      <div className="flex items-baseline justify-center gap-1">
                        <span className="text-4xl font-bold text-gray-900">{primaryPrice?.amount}</span>
                        {primaryPrice?.perMonth && <span className="text-gray-500 text-lg">/mo</span>}
                      </div>
                      {/* Billing description with unit/metric */}
                      {hasAnnualPrimary ? (
                        <div className="text-xs text-gray-500 mt-1">
                          {unitText ? (
                            <>per <span className="font-bold text-purple-600">{unitText}</span>/month</>
                          ) : (
                            <>per month</>
                          )}
                          <div className="text-xs text-gray-400">(billed annually)</div>
                        </div>
                      ) : unitText ? (
                        <div className="text-xs text-gray-500 mt-1">
                          per <span className="font-bold text-purple-600">{unitText}</span>
                        </div>
                      ) : null}
                    </>
                  );
                })()}

                {/* Monthly alternative price */}
                {monthlyAltDisplay && primaryCharge?.usage_type !== 'pay_as_you_go' && (
                  <div className="text-sm text-gray-500 mt-2">
                    or <span className="font-medium text-gray-700">{monthlyAltDisplay.amount}</span> billed monthly
                  </div>
                )}
              </>
            )}

            {/* Discount badge - show description/name instead of just amount */}
            {relevantDiscount && (
              <div className="mt-3">
                <Badge className="bg-green-100 text-green-700 border-green-200">
                  {relevantDiscount.description || relevantDiscount.name ||
                    `${relevantDiscount.amount}${relevantDiscount.amount_type === 'percent' ? '%' : ''} off`}
                </Badge>
              </div>
            )}
          </div>

          {/* Features List */}
          {features.length > 0 && (
            <div className="space-y-3">
              {features.map((feature, idx) => (
                <div key={idx} className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-gray-600">{feature}</span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </div>

      {/* Bottom section: Thresholds - aligned across cards */}
      <div className="px-6 pb-4 border-t border-gray-200 pt-4 overflow-hidden">
        {hasAnyThresholds ? (
          <>
            <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
              Included
            </div>
            <div className="space-y-3 overflow-hidden">
              {thresholdsToRender.map((threshold, idx) => {
                if (!threshold) {
                  return (
                    <div key={idx} className="flex items-start gap-2 text-sm min-h-[24px]">
                      {/* Empty slot - maintains alignment */}
                      <span className="text-gray-300">—</span>
                    </div>
                  );
                }

                const metricName = threshold.pricing_metric?.display_name || threshold.pricing_metric?.name;
                const fullText = `${threshold.quantity.toLocaleString()}${metricName ? ` ${metricName}` : ''}`;

                return (
                  <TooltipProvider key={idx} delayDuration={300}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div className="flex items-start gap-2 text-sm min-h-[24px] min-w-0">
                          <Check className="w-3.5 h-3.5 text-blue-500 flex-shrink-0 mt-0.5" />
                          <p className="text-gray-700 truncate flex-1 min-w-0 m-0">
                            <span className="font-semibold">{threshold.quantity.toLocaleString()}</span>
                            {metricName && (
                              <span className="text-gray-500"> {metricName}</span>
                            )}
                          </p>
                        </div>
                      </TooltipTrigger>
                      <TooltipContent side="top" className="max-w-xs">
                        <p>{fullText}</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                );
              })}
            </div>
          </>
        ) : (
          <div className="text-xs text-gray-400 italic">No usage limits</div>
        )}
      </div>
    </Card>
  );
};

// Add-on card component
const AddOnCard = ({ plan }: { plan: PricingSaasPlan }) => {
  const planName = plan.display_name || plan.plan_name || plan.name;
  const { monthlyCharge, annualCharge } = getChargesByBillingType(plan.charges || []);
  // Use annual as primary when available
  const primaryCharge = annualCharge || monthlyCharge;
  const primaryPrice = primaryCharge ? formatPriceValue(primaryCharge) : null;
  const hasAnnualPrimary = !!annualCharge;

  const isContactSales = !primaryPrice || primaryPrice.isContactSales;

  return (
    <Card className="border border-gray-200 hover:border-gray-300 transition-colors">
      <CardContent className="p-4">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
              <Plus className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <h4 className="font-medium text-gray-900">{planName}</h4>
              {primaryCharge?.pricing_metric?.display_name && (
                <p className="text-xs text-gray-500">{primaryCharge.pricing_metric.display_name}</p>
              )}
            </div>
          </div>
          <div className="text-right">
            {isContactSales ? (
              <span className="text-sm text-gray-600">Contact Sales</span>
            ) : (
              <div className="flex items-baseline gap-1">
                <span className="text-lg font-bold text-gray-900">{primaryPrice?.amount}</span>
                {primaryPrice?.perMonth && <span className="text-sm text-gray-500">/mo</span>}
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Helper to get plan price for sorting (returns null for free/contact sales)
const getPlanPrice = (plan: PricingSaasPlan): number | null => {
  const { monthlyCharge, annualCharge } = getChargesByBillingType(plan.charges || []);
  const primaryCharge = annualCharge || monthlyCharge;

  // Free plans
  const isFree = primaryCharge?.billing?.toLowerCase().includes('free') ||
                 primaryCharge?.billing?.toLowerCase().includes('freemium') ||
                 (primaryCharge?.price === 0);
  if (isFree) return null;

  // Contact sales (no price)
  const isContactSales = !primaryCharge ||
                         primaryCharge.price === null ||
                         primaryCharge.price === undefined;
  if (isContactSales) return null;

  // Ensure we return a numeric value
  const price = primaryCharge.price;
  return typeof price === 'number' ? price : Number(price) || 0;
};

export const PricingCardsDisplay = ({ plans, pageMetrics }: PricingCardsDisplayProps) => {
  // Separate main plans from add-ons and sort them
  const { mainPlans, addOns } = useMemo(() => {
    const main: PricingSaasPlan[] = [];
    const extras: PricingSaasPlan[] = [];

    plans.forEach(plan => {
      if (plan.add_on) {
        extras.push(plan);
      } else {
        main.push(plan);
      }
    });

    // Sort main plans: Free first, then by price ascending, then Contact Sales last
    main.sort((a, b) => {
      const priceA = getPlanPrice(a);
      const priceB = getPlanPrice(b);

      // Both null (free or contact sales) - maintain order
      if (priceA === null && priceB === null) return 0;

      // A is null (free/contact) - check if it's free
      if (priceA === null) {
        const { monthlyCharge: chargeA, annualCharge: annualA } = getChargesByBillingType(a.charges || []);
        const primaryA = annualA || chargeA;
        const isFreeA = primaryA?.billing?.toLowerCase().includes('free') ||
                       primaryA?.billing?.toLowerCase().includes('freemium') ||
                       (primaryA?.price === 0);
        return isFreeA ? -1 : 1; // Free first, contact sales last
      }

      // B is null (free/contact) - check if it's free
      if (priceB === null) {
        const { monthlyCharge: chargeB, annualCharge: annualB } = getChargesByBillingType(b.charges || []);
        const primaryB = annualB || chargeB;
        const isFreeB = primaryB?.billing?.toLowerCase().includes('free') ||
                       primaryB?.billing?.toLowerCase().includes('freemium') ||
                       (primaryB?.price === 0);
        return isFreeB ? 1 : -1; // Free first, contact sales last
      }

      // Both have prices - sort by price ascending
      return priceA - priceB;
    });

    return { mainPlans: main, addOns: extras };
  }, [plans]);

  // Align thresholds across all plans so they appear in the same order
  const { alignedPlans, canonicalOrder } = useMemo(() => {
    if (mainPlans.length === 0) {
      return { alignedPlans: [], canonicalOrder: [] };
    }

    // 1. Collect all unique metrics with metadata
    const metricMap = new Map<string, {
      key: string;
      count: number;
      metric: PricingSaasMetric | undefined;
      positions: number[]; // Track positions in each plan for ordering hints
    }>();

    mainPlans.forEach((plan, planIndex) => {
      plan.thresholds?.forEach((threshold, thresholdIndex) => {
        // Create a unique key for this metric
        const key = threshold.pricing_metric?.slug ||
                   threshold.pricing_metric?.name ||
                   threshold.pricing_metric_id ||
                   `threshold-${threshold.quantity}`;

        if (!metricMap.has(key)) {
          metricMap.set(key, {
            key,
            count: 0,
            metric: threshold.pricing_metric,
            positions: []
          });
        }

        const entry = metricMap.get(key)!;
        entry.count++;
        entry.positions.push(thresholdIndex);
      });
    });

    // 2. Sort metrics by:
    //    a) Frequency (most common first - appears in most plans)
    //    b) Average position (lower = appears earlier in plans)
    //    c) Alphabetically by display name
    const sortedMetrics = Array.from(metricMap.values()).sort((a, b) => {
      // First: by frequency (descending) - most common thresholds first
      if (b.count !== a.count) return b.count - a.count;

      // Second: by average position (ascending) - earlier positions first
      const avgPosA = a.positions.reduce((sum, p) => sum + p, 0) / a.positions.length;
      const avgPosB = b.positions.reduce((sum, p) => sum + p, 0) / b.positions.length;
      if (avgPosA !== avgPosB) return avgPosA - avgPosB;

      // Third: alphabetically
      const nameA = a.metric?.display_name || a.metric?.name || '';
      const nameB = b.metric?.display_name || b.metric?.name || '';
      return nameA.localeCompare(nameB);
    });

    const canonicalOrder = sortedMetrics.map(m => m.key);

    // 3. Create aligned threshold arrays for each plan
    const alignedPlans = mainPlans.map(plan => {
      // Build a map of this plan's thresholds by key
      const thresholdMap = new Map<string, PricingSaasThreshold>();
      plan.thresholds?.forEach(t => {
        const key = t.pricing_metric?.slug ||
                   t.pricing_metric?.name ||
                   t.pricing_metric_id ||
                   `threshold-${t.quantity}`;
        thresholdMap.set(key, t);
      });

      // Create aligned array following canonical order
      const alignedThresholds: (PricingSaasThreshold | null)[] = canonicalOrder.map(
        key => thresholdMap.get(key) || null
      );

      return {
        ...plan,
        alignedThresholds
      };
    });

    return { alignedPlans, canonicalOrder };
  }, [mainPlans]);

  // Height synchronization for card tops
  const topRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [topMinHeight, setTopMinHeight] = useState<number>(0);
  const [measurePass, setMeasurePass] = useState(0);

  // Ref setter for each card
  const setTopRef = (index: number) => (el: HTMLDivElement | null) => {
    topRefs.current[index] = el;
  };

  // Two-pass rendering: first measure, then apply
  useLayoutEffect(() => {
    if (mainPlans.length === 0) return;

    // Pass 1: Reset heights and trigger re-measure
    if (measurePass === 0) {
      topRefs.current.forEach(ref => {
        if (ref) ref.style.minHeight = '';
      });
      setTopMinHeight(0);
      // Trigger second pass after a tick
      setTimeout(() => setMeasurePass(1), 0);
      return;
    }

    // Pass 2: Measure natural heights and set max
    if (measurePass === 1) {
      const heights = topRefs.current
        .filter((ref): ref is HTMLDivElement => ref !== null)
        .map(ref => ref.offsetHeight);

      if (heights.length > 0) {
        const maxHeight = Math.max(...heights);
        setTopMinHeight(maxHeight);
      }
      setMeasurePass(2); // Done
    }
  }, [mainPlans, measurePass]);

  // Reset measure pass when plans change
  useLayoutEffect(() => {
    setMeasurePass(0);
  }, [mainPlans.length]);

  if (!plans || plans.length === 0) {
    return (
      <div className="text-center py-16">
        <div className="text-gray-400 text-lg">No pricing plans available</div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Main Pricing Tiers */}
      {alignedPlans.length > 0 && (
        <div className={`grid gap-6 items-stretch ${
          alignedPlans.length === 1 ? 'grid-cols-1 max-w-md mx-auto' :
          alignedPlans.length === 2 ? 'grid-cols-1 md:grid-cols-2 max-w-3xl mx-auto' :
          alignedPlans.length === 3 ? 'grid-cols-1 md:grid-cols-3' :
          alignedPlans.length === 4 ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-4' :
          'grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4'
        }`}>
          {alignedPlans.map((plan, index) => (
            <PricingTierCard
              key={plan.name || index}
              plan={plan}
              discounts={plan.discounts}
              topRef={setTopRef(index)}
              topMinHeight={topMinHeight}
              alignedThresholds={plan.alignedThresholds}
            />
          ))}
        </div>
      )}

      {/* Add-ons Section */}
      {addOns.length > 0 && (
        <div className="mt-12">
          <div className="flex items-center gap-2 mb-4">
            <Zap className="w-5 h-5 text-purple-500" />
            <h2 className="text-lg font-semibold text-gray-900">Add-ons</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {addOns.map((plan, index) => (
              <AddOnCard
                key={plan.name || index}
                plan={plan}
              />
            ))}
          </div>
        </div>
      )}

      {/* Metrics Section */}
      {pageMetrics && (
        <div className="mt-8 pt-8 border-t">
          <div className="flex items-center gap-2 mb-4">
            <Users className="w-5 h-5 text-blue-500" />
            <h2 className="text-lg font-semibold text-gray-900">Pricing Metrics</h2>
          </div>
          <div className="flex flex-wrap gap-2">
            {(Array.isArray(pageMetrics) ? pageMetrics : [pageMetrics]).map((metric, idx) => (
              <Badge key={idx} variant="outline" className="text-sm py-1.5 px-3">
                {metric.display_name || metric.name}
                {metric.unit && <span className="text-gray-500 ml-1">/ {metric.unit}</span>}
              </Badge>
            ))}
          </div>
        </div>
      )}

    </div>
  );
};
